/**
 * Type-safe translation keys. Locales other than "en" are loaded on demand via loadLocale().
 */
import { createSignal } from "solid-js";
// English UI is always loaded for fallback and initial render
import enUI from "../../messages/en/ui.json";

export type TranslationKey = keyof typeof enUI;

type TranslationDictionary = Record<TranslationKey, string>;
type PartialTranslationDictionary = Partial<TranslationDictionary>;

export const translations: Record<string, PartialTranslationDictionary> & { en: TranslationDictionary } = {
  en: enUI,
};

/** Bump this when a new locale is loaded so t() re-runs */
export const [getLoadedLocalesVersion, setLoadedLocalesVersion] = createSignal(0);

const uiLoaders: Record<string, () => Promise<{ default: PartialTranslationDictionary }>> = {
  ar: () => import("../../messages/ar/ui.json"),
  de: () => import("../../messages/de/ui.json"),
  es: () => import("../../messages/es/ui.json"),
  fr: () => import("../../messages/fr/ui.json"),
  he: () => import("../../messages/he/ui.json"),
  id: () => import("../../messages/id/ui.json"),
  it: () => import("../../messages/it/ui.json"),
  ko: () => import("../../messages/ko/ui.json"),
  ms: () => import("../../messages/ms/ui.json"),
  nl: () => import("../../messages/nl/ui.json"),
  no: () => import("../../messages/no/ui.json"),
  pt: () => import("../../messages/pt/ui.json"),
  ru: () => import("../../messages/ru/ui.json"),
  zh: () => import("../../messages/zh/ui.json"),
  "zh-CN": () => import("../../messages/zh-CN/ui.json"),
  "zh-TW": () => import("../../messages/zh-TW/ui.json"),
};

const uiLoads = new Map<string, Promise<void>>([["en", Promise.resolve()]]);

/**
 * Loads a locale's UI translations once, and a caller arriving mid-fetch waits for the same load.
 * A rejected load is forgotten, so the next call fetches again instead of finding the locale
 * marked loaded with no strings in it.
 */
export function loadLocale(locale: string, loaders = uiLoaders): Promise<void> {
  const inFlight = uiLoads.get(locale);
  if (inFlight) return inFlight;
  const load = loaders[locale];
  if (!load) return Promise.resolve();

  const pending = (async () => {
    const mod = await load();
    translations[locale] = { ...translations[locale], ...mod.default };
    setLoadedLocalesVersion((v) => v + 1);
  })().catch((err: unknown) => {
    uiLoads.delete(locale);
    throw err;
  });
  uiLoads.set(locale, pending);
  return pending;
}

export function getTranslation(locale: string, key: TranslationKey, params?: Record<string, string | number>): string {
  const dict = translations[locale] || translations.en;
  let translation = dict[key] || translations.en[key] || key;

  if (params) {
    for (const [paramKey, paramValue] of Object.entries(params)) {
      translation = translation.replace(new RegExp(`\\{${paramKey}\\}`, "g"), String(paramValue));
    }
  }

  return translation;
}
